import { Card, Col, Row, Dropdown, Form, Badge, Button, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useSalesforceStore from '../../../store/SalesforceStore';
// @ts-ignore
import './opportunityDocumentAreas.scss';
import { OpportunityCreditStatusEnum, OpportunityStageEnum, RoleEnum, VisibilityTagEnum } from '../../../api/models/Enums';
import React, { useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import SpinnerButton from '../../SpinnerButton/SpinnerButton';
import i18next from 'i18next';
import _ from 'lodash';
import toaster from '../../../utils/toaster';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { translateOpportunityCreditStatus } from '../../../utils/translations';
import Spinner from 'react-bootstrap/Spinner';
import Btn from '../../Btn/Btn';
import OpportunityAttachments from '../../../components/Opportunities/Opportunity/OpportunityAttchments';
import { OpportunityAttachment } from '../../../api/models/AccountEntity';
import { formatDate } from '../../../utils';
import { OptionDTO } from 'src/api';
import { FaCheck } from 'react-icons/fa';
import { FaAngleDown, FaTrash } from "react-icons/fa6";
import { Formik } from 'formik';
import RequiredFormLabel from 'src/components/RequiredFormLabel/RequiredFormLabel';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { useAuth } from 'src/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import OpportunityFilePreviewModal from './OpportunityFilePreviewModal';
import { DeleteOpportunityDocumentModal } from './DeleteOpportunityDocumentModal';
import { OpportunityChecklistItemDTO } from 'src/api/models/Opportunities/OpportunityChecklistItemDTO';
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import useOpportunityChecklist from 'src/hooks/useOpportunityChecklist';
import OpportunityChecklistActions from './OpportunityChecklistActions';
import { RequestOpportunityDocumentModal } from './RequestOpportunityDocumentModal';
import OpportunityDocumentUploaderModal from '../OpportunityDocumentUploaderModal/OpportunityDocumentUploaderModal';

const OpportunityCreditDocument = ({ data, className, setShowVehicleNotAvailableModal, isVehicleUnavailable, isOpportunityReadOnly, opportunityCreditDataLoading, opportunitySalesUser }) => {
  const { userRole } = useAuth();
  const { t } = useTranslation();
  const [creditStatus, setCreditStatus] = useState<string>(t("select_state"));
  const { opportunityData, accountSalesforce } = useSalesforceStore();
  const [updating, setUpdating] = useState<boolean>(false);
  const { apiClient, handleApiResponse } = useApiClient();
  const queryClient = useQueryClient();
  const [downloadingAttachment, setDownloadingAttachment] = useState<string | null>(null);
  const [removingAttachment, setRemovingAttachment] = useState<string | null>(null);
  const [filePreview, setFilePreview] = useState<{ isOpen: boolean; attachment?: any }>({ isOpen: false });
  const { showError } = useErrorToast();
  const navigate = useNavigate();
  const [deleteDocumentState, setDeleteDocumentState] = useState<{ isOpen: boolean; checklistItem?: OpportunityChecklistItemDTO | null }>({ isOpen: false });
  const { checklist, loadingChecklist, fetchChecklist, documentationFolder } = useOpportunityChecklist()
  const [showRequestDocumentModal, setShowRequestDocumentModal] = useState(false);
  const [showUploadDocsModal, setShowUploadDocsModal] = useState<boolean>(false);
  const isChecklistCreated = !!checklist?.createdAt;

  const { mutateAsync: changeCreditStatus, isPending: changeCreditStatusPending } = useMutation({
    mutationFn: async (newStatus: string) => {
      return await apiClient.salesforceOpportunityController.changeCreditStatus(opportunityData?.Id, newStatus);
    },
    onSuccess: async (data, newStatus) => {
      try {
        toaster.success(t('credit_status_updated', { status: t(newStatus, newStatus) }));
      } catch (error) {
        console.warn(error);
      }
    },
    onError: (error) => {
      console.error('Error adding message:', error);
      toaster.error(t('failed_to_add_message'));
    },
  });

  const downloadAttachment = async (a: OpportunityAttachment) => {
    setDownloadingAttachment(a.Id);
    const presignedReponse = await handleApiResponse(apiClient.salesforceOpportunityController.getOpportunityAttachment(a.Id));

    if (presignedReponse && presignedReponse.downloadUrl) {
      const link = document.createElement('a');
      link.href = presignedReponse.downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setDownloadingAttachment(null);
  };

  const previewAttachment = async (attachment?: OpportunityAttachment) => {
    if (!attachment) return;
    try {
      setDownloadingAttachment(attachment.Id);

      const notPdf = !attachment?.Title?.endsWith('.pdf');
      const presignedReponse = await handleApiResponse(apiClient.salesforceOpportunityController.getOpportunityAttachment(attachment.Id));

      if (!presignedReponse?.downloadUrl) {
        console.warn('No download url for attachment ', attachment);
        return;
      }

      if (notPdf) {
        const link = document.createElement('a');
        link.href = presignedReponse.downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      setFilePreview({ isOpen: true, attachment: { ...attachment, url: presignedReponse.downloadUrl } })
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingAttachment(null);
    }
  }

  useEffect(() => {
    if (data && data?.statoCredito) {
      setCreditStatus(OpportunityCreditStatusEnum[data?.statoCredito] || t('select_state'))
    }
  }, [data, t])

  useEffect(() => { fetchChecklist(opportunityData) }, [opportunityData]);

  const downloadAllDocuments = () => {
    handleApiResponse(apiClient.salesforceOpportunityController.downloadAllDocuments(opportunityData.Id))
      .then((res) => {
        if (res && res.downloadUrl) {
          // Create a temporary link and trigger download in a new tab
          const link = document.createElement('a');
          link.href = res.downloadUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .catch((err) => {
        console.error('Error downloading all documents:', err);
        toaster.error(t('error_downloading_documents'));
      });
  }

  const deriveStateButtonText = () => {
    if (updating) {
      return <Spinner animation="border" role="output" size="sm" />;
    }
    if (changeCreditStatusPending) {
      return <Spinner animation="border" role="output" size="sm" />;
    }
    return translateOpportunityCreditStatus(creditStatus);
  };

  const trackChangeStateTime = () => {
    try {
      localStorage.setItem(`lastChangedStateTime`, Date.now().toString());
    } catch (error) {/* ignore */ }
  }

  const onChangeCreditStatus = async (newStatus: string) => {
    if (newStatus === data?.statoCredito) {
      return;
    }

    if (newStatus === OpportunityCreditStatusEnum.credit_sent && isVehicleUnavailable) {
      const oppNewStage = OpportunityCreditStatusEnum.credit_sent === newStatus
        ? OpportunityStageEnum.credit_score_check
        : null;

      setShowVehicleNotAvailableModal?.(true, oppNewStage)
      return;
    }

    setUpdating(true);
    try {
      await changeCreditStatus(newStatus);
      trackChangeStateTime();
      if (newStatus === OpportunityCreditStatusEnum.credit_sent && opportunityData?.ID_Vehicle_HMP__c) {
        const optionRequest: OptionDTO = {
          cliente: accountSalesforce?.Name,
          opportunityId: opportunityData.Id,
          vehicleId: opportunityData.ID_Vehicle_HMP__c,
          ragione_sociale: accountSalesforce.Codice_Fiscale__c || accountSalesforce.Partita_IVA__c || '',
          noleggiatore: opportunityData.Noleggiatore__c || '',
          document: [],
        };
        await handleApiResponse(
          apiClient.vehicleActionsController.postVehiclesActions(optionRequest)
        );
      }
      setCreditStatus(OpportunityCreditStatusEnum[newStatus as keyof typeof OpportunityCreditStatusEnum]);
      await queryClient.invalidateQueries({ queryKey: ['opportunityCreditData'] });
      navigate(0);
    } catch (error) {
      console.error('Error updating credit status:', error);
      toaster.error(t('failed_to_update_credit_status'));
    } finally {
      setUpdating(false);
    }
  }

  const removeAttachment = async (a: OpportunityAttachment) => {
    try {
      setRemovingAttachment(a.Id);
      await handleApiResponse(apiClient.salesforceOpportunityController.removeOpportunityAttachment(a.ContentDocumentId));
      queryClient.invalidateQueries({ queryKey: ['opportunityData'] });
      queryClient.invalidateQueries({ queryKey: ['opportunityCreditData'] });
      fetchChecklist(opportunityData);
    } catch (error) {
      showError(error, t('error_removing_attachment'));
    } finally {
      setRemovingAttachment(null);
    }
  };

  const requestDocumentation = async () => {
    try {
      setUpdating(true);
      await handleApiResponse(apiClient.salesforceOpportunityController.requestDocumentation(opportunityData.Id));
      toaster.success(t('request_send_successfully'));
      queryClient.invalidateQueries({ queryKey: ['opportunityData'] });
      queryClient.invalidateQueries({ queryKey: ['opportunityCreditData'] });
      fetchChecklist(opportunityData)
    } catch (error) {
      showError(error, t('error_requesting_documentation'))
    } finally {
      setUpdating(false);
    }
  }

  const checklistDocuments = useMemo(() => {
    if (!checklist) return [];
    const fileItems = (checklist?.items || []).filter(item => item.isFileType).map(checkItem => {
      checkItem.sfAttachment = opportunityData?.attachments?.find((item) => item.Title === checkItem.name)
      return checkItem;
    });

    const varItems = (checklist.items || []).filter(item => item.isVariableType);
    return fileItems.concat(varItems);

  }, [checklist, opportunityData])

  const checklistInitValues = useMemo(() => {
    const values = {};
    const checkItems = (checklist?.items || []).filter(item => item.isVariableType);
    for (const item of checkItems) {
      item.name && (values[item.name] = item.value || '');
    }
    return values;
  }, [checklist])

  const canHandleAttachments = useMemo(() => {
    if (!isChecklistCreated) {
      return false;
    }

    const allowedRole = [
      RoleEnum.MASTER,
      RoleEnum.BACKOFFICE,
      RoleEnum.AREA_MANAGER,
      RoleEnum.SALES_INTERNAL,
      RoleEnum.SALES,
      RoleEnum.SALES_DEALER
    ];

    return allowedRole.includes(userRole)
  }, [userRole, isChecklistCreated])

  const canEditChecklistItem = [
    RoleEnum.MASTER,
    RoleEnum.BACKOFFICE,
    RoleEnum.AREA_MANAGER,
  ].includes(userRole)

  const doDeleteChecklistItem = async (reason: string, checklistItem?: OpportunityChecklistItemDTO) => {
    if (!checklistItem) {
      setDeleteDocumentState((prev) => ({
        ...prev,
        isOpen: false,
        checklistItem: null,
      }));
      return;
    }

    const rejectPayload = _.omit({ ...checklistItem, reason }, ['sfAttachment', 'isFileType', 'isOk', 'isVariableType']);

    try {
      setUpdating(true);
      await handleApiResponse(apiClient.salesforceOpportunityController.rejectDocument(opportunityData.Id, rejectPayload));
      queryClient.invalidateQueries({ queryKey: ['opportunityData'] });
      queryClient.invalidateQueries({ queryKey: ['opportunityCreditData'] });
      fetchChecklist(opportunityData);
      toaster.success(t('removed_checklist_item'));
    } catch (error) {
      showError(error, t('error_removing_checklist_item'))
    } finally {
      setUpdating(false);
      setDeleteDocumentState(prev => ({ ...prev, checklistItem: null, isOpen: false }))
    }
  }

  const externalBoEndpoint = useMemo(() => {
    const origin = window.location.origin;
    return `${origin}/documents/index.html#/hmp/${opportunityData.Id}/documentation`
  }, [opportunityData]);

  const isOpportunityClosed = useMemo(() => {
    return [
      OpportunityStageEnum.closed_by_credit_score_check,
      OpportunityStageEnum.closed_won,
      OpportunityStageEnum.closed_by_customer,
    ].includes(opportunityData?.StageName)
  }, [opportunityData]);

  const canDownloadAllDocuments = useMemo(() => {
    return checklistDocuments.filter(checkItem => checkItem.isFileType && checkItem.isOk).length > 0
  }, [checklistDocuments])

  const uploadDocumentsOnExternal = async () => {
    try {
      if (isChecklistCreated) {
        externalBoEndpoint && window.open(externalBoEndpoint, '_blank');
        return;
      }

      setUpdating(true);
      await handleApiResponse(apiClient.salesforceOpportunityController.requestDocumentation(opportunityData.Id, false));
      queryClient.invalidateQueries({ queryKey: ['opportunityData'] });
      queryClient.invalidateQueries({ queryKey: ['opportunityCreditData'] });
      fetchChecklist(opportunityData)
      externalBoEndpoint && window.open(externalBoEndpoint, '_blank');
    } catch (error) {
      showError(error, t('error_creating_opportunity_checklist'))
    } finally {
      setUpdating(false);
    }
  }

  return (
    <Card className={'mb-4 ' + className || ''}>
      {!loadingChecklist && <Card.Body>
        <Card.Subtitle className="mb-4 mt-2">
          <div className="d-flex align-items-center justify-content-between fs-5">
            <div className="fw-bold">{t('credit_management')}</div>
            <Row>
              <Col>
                <Dropdown className="ms-1 align-text-bottom d-inline-block action-dropdown">
                  <Dropdown.Toggle
                    disabled={[RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL, RoleEnum.SEGNALATORE].includes(userRole) || isOpportunityReadOnly}
                    className="btn">
                    {deriveStateButtonText()}
                    <FaAngleDown style={{ width: 20, height: 20 }} className='ms-2' />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="position-fixed">
                    {Object.keys(OpportunityCreditStatusEnum).map((status) => (
                      <Dropdown.Item
                        key={status}
                        className="d-flex align-items-start justify-content-between"
                        disabled={status === OpportunityCreditStatusEnum.credit_sent && [OpportunityStageEnum.closed_by_credit_score_check, OpportunityStageEnum.closed_won].includes(opportunityData.StageName)}
                        onClick={() => onChangeCreditStatus(status)}
                      >
                        <div>
                          {translateOpportunityCreditStatus(status)}
                        </div>
                        {status === data?.statoCredito ? <FaCheck /> : null}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>
          </div>
          <OpportunityChecklistActions
            checklist={checklist}
            isOpportunityClosed={isOpportunityClosed}
            isBusy={updating}
            isOpportunityReadOnly={isOpportunityReadOnly}
            actions={{
              openRequestDocumentModal: setShowRequestDocumentModal,
              requestDocumentation,
              uploadDocumentsOnExternal,
            }}
          />
        </Card.Subtitle>
        {isChecklistCreated &&
          <div className="my-4 d-flex align-items-center justify-content-between">
            <div className="fw-bold fs-5" style={{ marginTop: 32 }}>{t('checklist_documents')}</div>
            {canDownloadAllDocuments && <div className="d-flex align-items-center" style={{ marginTop: 32 }}>
              <button
                disabled={updating}
                type="button"
                className="d-flex btn p-0 border-0 bg-transparent"
                title={t('download_all')}
                onClick={downloadAllDocuments}
              >
                <div className="fw-bold">{t('download_all')}</div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9 2a1 1 0 0 1 2 0v8.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4A1 1 0 1 1 6.707 8.293L9 10.586V2z" />
                  <path d="M3 16a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1z" />
                </svg>
              </button>
            </div>}
          </div>
        }
        {checklistDocuments.filter(checkItem => checkItem.isFileType).map((checkItem, idx) => (
          <React.Fragment key={idx}>
            <Row className="align-items-center mb-3">
              <Col md="auto">
                <div className="d-flex align-items-center">
                  {checkItem.isOk && <IoIosCheckmarkCircleOutline className="text-success me-2" size={24} />}
                  <span className="me-2">{parseChecklistItemLabel(checkItem)}</span>
                </div>
                {checkItem.isOk ? (
                  <span
                    className="text-decoration-underline ms-4 ps-2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      previewAttachment(checkItem.sfAttachment);
                    }}
                  >
                    ({checkItem.name})
                  </span>
                ) : null}
              </Col>

              <Col className="ms-auto" md="auto">
                {checkItem.isOk && checkItem.createdAt
                  ? <Badge bg="secondary" className="m-2">{formatDate(checkItem.createdAt, 'it-IT', true)}</Badge>
                  : null
                }
                {checkItem.isOk && checkItem.sfAttachment && (
                  <Btn
                    variant="link"
                    onClick={() => checkItem.sfAttachment && downloadAttachment(checkItem.sfAttachment)}
                  >
                    <SpinnerButton label={i18next.t('download')} busy={false} />
                  </Btn>)
                }
                {checkItem.isOk && canEditChecklistItem && checkItem.sfAttachment && !isOpportunityClosed && <Button
                  variant="link"
                  disabled={updating || isOpportunityReadOnly}
                  onClick={() => {
                    const attachment = checkItem.sfAttachment;
                    if (!attachment) return;
                    setDeleteDocumentState(prev => ({ ...prev, checklistItem: checkItem, isOpen: true }))
                  }}
                >
                  <FaTrash />
                </Button>}
              </Col>
            </Row>
            <hr />
          </React.Fragment>
        ))}

        <Formik
          initialValues={checklistInitValues}
          onSubmit={(values) => { }}
        >
          {({ values, handleChange }) => (
            <Form autoComplete='off'>
              {checklistDocuments.filter(item => item.isVariableType).map((checkItem, idx) => {
                return <React.Fragment key={idx}>
                  <Row className="mb-3 align-items-center">
                    <Col md="10">
                      <Form.Group className="mb-2">
                        <RequiredFormLabel label={checkItem.isOk
                          ? (<>
                            <IoIosCheckmarkCircleOutline className="text-success me-2" size={24} />
                            {i18next.t(checkItem.name!)}
                          </>)
                          : i18next.t(checkItem.name!)} />
                        <Form.Control
                          id={checkItem.name}
                          name={checkItem.name}
                          disabled={true}
                          type="text"
                          value={checkItem.value || ""}
                        />
                      </Form.Group>
                    </Col >
                    {canEditChecklistItem && checkItem.isOk && !isOpportunityClosed && <Col className="ms-auto" md="auto">
                      <Button
                        variant="link"
                        disabled={updating || isOpportunityReadOnly}
                        onClick={() => {
                          setDeleteDocumentState(prev => ({ ...prev, checklistItem: checkItem, isOpen: true }))
                        }}
                      >
                        <FaTrash />
                      </Button>
                    </Col>}
                  </Row>
                  <hr />
                </React.Fragment>
              })}
            </Form>
          )}
        </Formik>
        <OpportunityAttachments
          isLegacy={false}
          customTitle={canHandleAttachments ? undefined :i18next.t('uploaded_quotes')}
          setShowUploadDocsModal={canHandleAttachments ? setShowUploadDocsModal : undefined}
          opportunityResponse={opportunityData}
          loadedDocuments={documentationFolder}
          updating={updating}
          downloadingAttachment={downloadingAttachment}
          downloadAttachment={downloadAttachment}
          removeAttachment={removeAttachment}
          removingAttachment={removingAttachment}
          fetchChecklist={() => fetchChecklist(opportunityData)}
          previewAttachment={(attachment) => previewAttachment(attachment)}
        />
      </Card.Body>}

      <OpportunityFilePreviewModal
        isOpen={filePreview.isOpen}
        closePreview={() => setFilePreview({ isOpen: false })}
        attachment={filePreview.attachment}
      />

      {deleteDocumentState?.checklistItem && (
        <DeleteOpportunityDocumentModal
          checklistItem={deleteDocumentState.checklistItem}
          isOpen={deleteDocumentState.isOpen}
          closeModal={() => {
            setDeleteDocumentState(prev => ({ ...prev, isOpen: false, checklistItem: null }))
          }}
          doDeleteDocument={(reason) => {
            deleteDocumentState.checklistItem && doDeleteChecklistItem(reason, deleteDocumentState.checklistItem)
          }}
        />
      )}

      {showRequestDocumentModal &&
        <RequestOpportunityDocumentModal
          opportunityId={opportunityData.Id}
          isOpen={showRequestDocumentModal}
          onSuccess={() => fetchChecklist(opportunityData)}
          closeModal={() => {
            setShowRequestDocumentModal(false)
          }}
        />}
      <OpportunityDocumentUploaderModal
        show={showUploadDocsModal}
        hasAllDocuments={() => false}
        opportunity={opportunityData ?? undefined}
        onHide={() => {
          setShowUploadDocsModal(false);
        }}
        onSuccess={() => {
          setShowUploadDocsModal(false);
          queryClient.invalidateQueries({ queryKey: ['opportunityData'] });
          queryClient.invalidateQueries({ queryKey: ['opportunityCreditData'] });
        }}
      />
    </Card>
  );
};

function parseChecklistItemLabel(checkItem: OpportunityChecklistItemDTO) {
  const extensions = [
    '.doc',
    '.docx',
    '.pdf',
    '.png',
    '.tiff',
    '.jpg',
    '.jpeg',
    '.gif',
    '.bmp',
    '.txt',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.zip',
    '.rar',
  ];
  const regex = new RegExp(
    `(${extensions.map((ext) => ext.replace('.', '\\.')).join('|')})+$`,
    'i'
  );
  return checkItem.type?.replace(regex, '');
}

export default OpportunityCreditDocument;
